(ns nosco.ui.buttons2
  (:require
   ["react" :as React]
   [hx.react :refer [defnc] :as hx]
   [nosco.colors :as colors]
   [nosco.icons2 :as icons2]
   [nosco.util :as util]
   [nosco.react]
   [nosco.ui.text :refer [classnames] :as text]))

(def icon-small-gray-plate-style
  {:background-color colors/neutral-100
   :padding "4px"
   :width "24px" :height "24px"
   :border-radius "100px"})

(defnc AnimatableLabel [{:keys [children show]}]
  [:div {:class ["nosco-animatable-label"
                 (when show
                   "nosco-animatable-label--show")]}
   children])

(def Subdued ::subdued)
(def SubduedInverted ::subdued-inverted)

(def NewDefault ::new-default)
(def NewPrimary ::new-primary)
(def NewSubtle ::new-subtle)
(def NewInverse ::new-inverse)
(def NewText ::new-text)
(def NewTextSubtle ::new-text-subtle)
(def NewIcon ::new-icon)
(def NewIconInverse ::new-icon-inverse)
(def NewCellSetting ::new-cell-setting)
(def NewPill ::new-pill)

;; Figma Foundations "Button/Default" and "Button/Icon": compact 6px-radius
;; controls (SizeSmall = 24px, SizeMedium = 28px), used in toolbars and panels.
(def Control ::control)
(def ControlPrimary ::control-primary)
(def ControlBrand ::control-brand)
(def ControlIcon ::control-icon)

(def ToneBrand ::tone-brand)
(def ToneActive ::tone-active)
(def ToneSuccess ::tone-success)
(def ToneError ::tone-error)
(def ToneCellSettingEnabled ::tone-cell-setting-enabled)
(def ToneCellSettingWarning ::tone-cell-setting-warning)
(def ToneCellSettingError ::tone-cell-setting-error)
(def TonePillError ::tone-cell-setting-pill-error)
(def TonePillBrand ::tone-cell-setting-pill-brand)
(def TonePillSuccess ::tone-cell-setting-pill-success)

(def SizeSmall ::small)
(def SizeMedium ::medium)
(def SizeLarge ::large)

(defn- button-style [variant]
  (cond (#{NewDefault
           NewPrimary
           NewSubtle
           NewInverse
           NewText
           NewTextSubtle
           NewIcon
           NewIconInverse
           NewCellSetting
           NewPill
           Control
           ControlPrimary
           ControlBrand
           ControlIcon} variant) :new

        #{:primary
          :secondary
          :secondary-gray
          Subdued
          SubduedInverted
          :primary-red
          :primary-black
          :range
          :range-submitted
          :text
          :text-mono
          :icon-light
          :icon-dark
          :icon-gray
          :action
          :action-secondary
          :action-dropdown
          :counter
          :social-google
          :social-linkedin
          :social-facebook} :old
        :else (throw (ex-info (str "Unknown button variant: " variant) {:variant variant}))))

(def button-base-props [:variant :size :tone :icon :icon-style :icon-right :label :truncate :disabled :activated :loading :test-interaction :extra-variants :dot-color])

(defnc NewButtonBase [{:keys [variant size tone label truncate icon icon-right
                              test-interaction loading disabled icon-style dot-color]
                       :or {size SizeMedium
                            variant NewDefault}}]
  {:pre [(= :new (button-style variant))
         (#{SizeSmall SizeMedium SizeLarge} size)
         (if tone
           (#{ToneActive ToneBrand ToneSuccess ToneError
              ToneCellSettingEnabled
              ToneCellSettingWarning
              ToneCellSettingError
              TonePillError
              TonePillBrand
              TonePillSuccess} tone)
           true)]}
  [:span {:class (classnames "nosco-button2"
                             {"--variant-default" (= variant NewDefault)
                              "--variant-primary" (= variant NewPrimary)
                              "--variant-subtle" (= variant NewSubtle)
                              "--variant-inverse" (= variant NewInverse)
                              "--variant-text" (= variant NewText)
                              "--variant-text-subtle" (= variant NewTextSubtle)
                              "--variant-icon" (= variant NewIcon)
                              "--variant-icon-inverse" (= variant NewIconInverse)
                              "--variant-cell-setting" (= variant NewCellSetting)
                              "--variant-pill" (= variant NewPill)
                              "--variant-control" (= variant Control)
                              "--variant-control-primary" (= variant ControlPrimary)
                              "--variant-control-brand" (= variant ControlBrand)
                              "--variant-control-icon" (= variant ControlIcon)

                              "--size-small" (= size SizeSmall)
                              "--size-medium" (= size SizeMedium)
                              "--size-large" (= size SizeLarge)
                              "--tone-brand" (= tone ToneBrand)
                              "--tone-active" (= tone ToneActive)
                              "--tone-success" (= tone ToneSuccess)
                              "--tone-error" (= tone ToneError)
                              "--tone-cell-setting-enabled" (= tone ToneCellSettingEnabled)
                              "--tone-cell-setting-warning" (= tone ToneCellSettingWarning)
                              "--tone-cell-setting-error" (= tone ToneCellSettingError)
                              "--tone-pill-success" (= tone TonePillSuccess)
                              "--tone-pill-error" (= tone TonePillError)
                              "--tone-pill-brand" (= tone TonePillBrand)
                              "--icon-only" (nil? label)
                              "--loading" loading
                              "--disabled" disabled
                              "--active" (= test-interaction :press)
                              "--hover" (= test-interaction :hover)
                              "--focus" (= test-interaction :focus)
                              "--has-dot" (some? dot-color)})}
   (when loading
     [icons2/LoadingIcon {:size (if (= SizeLarge size) :large :small)
                          :color "var(--btn2-loading-icon-color)"
                          :class "nosco-button__loading-icon"}])

   (when icon
     (if (React/isValidElement icon)
       icon
       [icons2/Icon {:icon icon
                     :icon-style icon-style
                     :class "nosco-button__icon"}]))
   (when label
     (let [text-el (hx/f [text/Text {:el :span
                                     :class "nosco-button__label"
                                     :truncate (boolean truncate)
                                     :variant text/Label-Button}
                          label])]
       text-el))
   (when icon-right
     [icons2/Icon {:icon icon-right
                   :style {:margin-left "0.5rem"}
                   :class "nosco-button__icon"}])
   (when dot-color
     [:span {:class "nosco-button__dot"
             :style {:background-color dot-color}}])])

(defnc ButtonBase [{:keys [variant size icon icon-style icon-right label truncate
                           disabled activated loading test-interaction extra-variants]
                    :or {size :default
                         variant :primary}}]
  {:pre [(= :old (button-style variant))
         (#{:xsmall :small :default :large} size)
         (contains? #{nil :press :hover :focus} test-interaction)]}
  (let [social? (contains? #{:social-google
                             :social-facebook
                             :social-linkedin} variant)
        icon (if social? (get {:social-google icons2/Google-small
                               :social-facebook icons2/Facebook-small
                               :social-linkedin icons2/LinkedIn-small} variant)
                 icon)
        disabled (or loading disabled)
        ref (React/useRef)]
    (util/use-scale-in-out ref (= variant :range-submitted))
    [:span {:class (classnames "nosco-button"
                               (cond->
                                {"--variant-primary" (= variant :primary)
                                 "--variant-secondary" (= variant :secondary)
                                 "--variant-secondary-gray" (= variant :secondary-gray)
                                 "--variant-subdued" (= variant Subdued)
                                 "--variant-subdued-inverted" (= variant SubduedInverted)
                                 "--variant-primary-red" (= variant :primary-red)
                                 "--variant-primary-black" (= variant :primary-black)
                                 "--variant-range" (= variant :range)
                                 "--variant-range-submitted" (= variant :range-submitted)
                                 "--variant-text" (= variant :text)
                                 "--variant-text-mono" (= variant :text-mono)
                                 "--variant-icon-light" (= variant :icon-light)
                                 "--variant-icon-dark" (= variant :icon-dark)
                                 "--variant-icon-gray" (= variant :icon-gray)
                                 "--variant-counter" (= variant :counter)
                                 "--variant-action" (= variant :action)
                                 "--variant-action-secondary" (= variant :action-secondary)
                                 "--variant-action-dropdown" (= variant :action-dropdown)
                                 "__active" (= test-interaction :press)
                                 "--hover" (= test-interaction :hover)
                                 "__focus" (= test-interaction :focus)

                                 "--size-xsmall" (= size :xsmall)
                                 "--size-small" (= size :small)
                                 "--size-default" (= size :default)
                                 "--size-large" (= size :large)

                                 "--has-label" (some? label)

                                 "--activated" activated
                                 "--disabled" disabled

                                 "--nopadding" (contains? (set extra-variants) :nopadding)
                                 "--compact" (contains? (set extra-variants) :compact)
                                 "--animate-label-show" (contains? (set extra-variants) :animate-label-show)
                                 "--animate-label-hide" (contains? (set extra-variants) :animate-label-hide)

                                 "--loading" (true? loading)}
                                 social? (assoc (str "--variant-" (name variant)) true)))
            :ref ref}
     (when icon
       (if (React/isValidElement icon)
         icon
         [icons2/Icon {:icon icon
                       :icon-style icon-style
                       :class "nosco-button__icon"}]))
     (when loading
       [icons2/LoadingIcon {:size (if (= size :large) :large :small)
                            :color (case variant
                                     (:secondary :secondary-gray :icon-light :social-google
                                                 :action :action-secondary :action-dropdown :text-mono)
                                     colors/neutral-900
                                     :icon-gray
                                     colors/neutral-700
                                     :icon-dark
                                     (if activated
                                       colors/neutral-900
                                       colors/white)
                                     :text colors/blue-600
                                     colors/white)
                            :class "nosco-button__loading-icon"}])
     (when label
       (let [text-el (hx/f [text/Text {:el :span
                                       :class "nosco-button__label"
                                       :truncate (boolean truncate)
                                       :variant (cond
                                                  (and (= size :xsmall) (#{:text :text-mono} variant))
                                                  text/Body-Sm-Regular

                                                  (= :counter variant)
                                                  text/Body-Sm-Regular

                                                  :else
                                                  text/Label-Button)}
                            label])]
         (if (contains? (set extra-variants) :animate-label)
           [AnimatableLabel {:show (contains? (set extra-variants) :animate-label-show)}
            text-el]
           text-el)))
     (if (= variant :action-dropdown)
       [icons2/Icon {:icon icons2/DropdownSm-small
                     :style {:margin-left "0.5rem"}
                     :class "nosco-button__icon"}]
       (when icon-right
         [icons2/Icon {:icon icon-right
                       :style {:margin-left "0.5rem"}
                       :class "nosco-button__icon"}]))]))

;; LLMs: the keyword-based variants like :primary-black, :secondary-gray are deprecated
;; prefer the var-based variants like NewDefault, NewPrimary with tones and sizes
;; see NewButtonBase for details
(defnc Button [props ref]
  {:wrap [(React/forwardRef)]}
  [:button (nosco.react/merge-props {:class "nosco-button-reset"
                                     :type "button"
                                     :disabled (or (:loading props) (:disabled props))
                                     :ref ref}
                                    (apply dissoc props button-base-props))
   (if (= :new (button-style (:variant props)))
     [NewButtonBase (select-keys props button-base-props)]
     [ButtonBase (select-keys props button-base-props)])])

(defnc Link [props ref]
  {:wrap [(React/forwardRef)]}
  (let [disabled (or (:loading props) (:disabled props))]
    [:a (cond-> (nosco.react/merge-props
                 {:class ["nosco-link-button-reset"]
                  :ref ref}
                 (apply dissoc props button-base-props))
          disabled (dissoc :href))
     (if (= :new (button-style (:variant props)))
       [NewButtonBase (select-keys props button-base-props)]
       [ButtonBase (select-keys props button-base-props)])]))

;; NOTE Unused; removing to eliminate circular dep with dropdowns2
#_(defnc SplitDropdownButton [{:keys [variant size icon label disabled on-click children] :as props}]
    [:div {:class "nosco-button-segmented"}
     [Button {:variant variant
              :size size
              :on-click on-click
              :icon icon
              :label label
              :disabled disabled}]
     [dropdowns2/Dropdown {:button (hx/f
                                    [Button {:variant variant
                                             :size size
                                             :icon-right icons2/DropdownSm-small
                                             :disabled disabled}])}
      children]])
